import React from 'react';
import { z } from 'zod';

import { Box, Form, Stack } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

import { ColorPicker } from './ColorPicker';

// --- Brand color ---

const brandColorSchema = z.object({
	brandColor: z
		.string()
		.regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color (e.g., #0066CC)')
		.optional()
		.or(z.literal('')),
});

function BrandColorForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (
		data: z.infer<typeof brandColorSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					brandColor: data.brandColor || undefined,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-brand-color'
				schema={brandColorSchema}
				defaultValues={{
					brandColor: settings?.embeddedWebsiteAssistant?.brandColor ?? '',
				}}
				onSubmit={handleSubmit}
				title='Brand color'
				titleLevel='minor'
				description='Choose an accent color for the chat widget. Pick a preset or enter a custom hex code.'
				titleWeight='semibold'
				showSaveAndClear>
				<Form.Item
					schemaField='brandColor'
					descriptionText=''
					useTooltipForDescription={false}>
					<ColorPicker />
				</Form.Item>
			</Form>
		</Box>
	);
}

// --- Font family ---

const FONT_FAMILIES = [
	{ id: '', name: 'System default', stack: '' },
	{ id: 'Arial, sans-serif', name: 'Arial', stack: 'Arial, sans-serif' },
	{
		id: 'Helvetica, Arial, sans-serif',
		name: 'Helvetica',
		stack: 'Helvetica, Arial, sans-serif',
	},
	{ id: 'Verdana, Geneva, sans-serif', name: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
	{ id: 'Tahoma, Geneva, sans-serif', name: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
	{ id: '"Trebuchet MS", sans-serif', name: 'Trebuchet MS', stack: '"Trebuchet MS", sans-serif' },
	{ id: 'Georgia, serif', name: 'Georgia', stack: 'Georgia, serif' },
	{
		id: '"Times New Roman", Times, serif',
		name: 'Times New Roman',
		stack: '"Times New Roman", Times, serif',
	},
	{ id: 'Garamond, serif', name: 'Garamond', stack: 'Garamond, serif' },
	{
		id: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
		name: 'Palatino',
		stack: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
	},
	{
		id: '"Courier New", Courier, monospace',
		name: 'Courier New',
		stack: '"Courier New", Courier, monospace',
	},
	{
		id: '"Lucida Console", Monaco, monospace',
		name: 'Lucida Console',
		stack: '"Lucida Console", Monaco, monospace',
	},
	{
		id: '"Lucida Sans Unicode", "Lucida Grande", sans-serif',
		name: 'Lucida Sans',
		stack: '"Lucida Sans Unicode", "Lucida Grande", sans-serif',
	},
	{
		id: '"Segoe UI", Roboto, sans-serif',
		name: 'Segoe UI',
		stack: '"Segoe UI", Roboto, sans-serif',
	},
	{ id: 'Inter, sans-serif', name: 'Inter', stack: 'Inter, sans-serif' },
];

const FONT_FAMILY_OPTIONS = FONT_FAMILIES.map((font) => ({
	id: font.id,
	label: font.stack ? <span style={{ fontFamily: font.stack }}>{font.name}</span> : font.name,
	selectedLabel: font.stack ? (
		<span style={{ fontFamily: font.stack }}>{font.name}</span>
	) : (
		font.name
	),
}));

const fontFamilySchema = z.object({
	fontFamily: z.string().optional().or(z.literal('')),
});

function FontFamilyForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (
		data: z.infer<typeof fontFamilySchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					fontFamily: data.fontFamily || undefined,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-font-family'
				schema={fontFamilySchema}
				defaultValues={{
					fontFamily: settings?.embeddedWebsiteAssistant?.fontFamily ?? '',
				}}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='Font'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Choose a web-safe font for your chat widget text.'>
				<Form.Field.Dropdown
					schemaField='fontFamily'
					labelText='Font family'
					options={FONT_FAMILY_OPTIONS}
					placeholder='System default'
				/>
			</Form>
		</Box>
	);
}

// --- Dark mode ---

const darkModeSchema = z.object({
	darkMode: z.boolean().optional(),
});

function DarkModeForm() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const handleSubmit = async (
		data: z.infer<typeof darkModeSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					darkMode: data.darkMode,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-dark-mode'
				schema={darkModeSchema}
				defaultValues={{
					darkMode: settings?.embeddedWebsiteAssistant?.darkMode ?? false,
				}}
				onSubmit={handleSubmit}
				showSaveAndClear
				title='Dark mode'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Use a dark color scheme for the chat widget background and surfaces.'
				toggle={{ fieldName: 'darkMode' }}
			/>
		</Box>
	);
}

// --- Combined export ---

export function AppearanceSection() {
	return (
		<Stack itemsSpacing='16'>
			<BrandColorForm />
			<FontFamilyForm />
			<DarkModeForm />
		</Stack>
	);
}
